import React, { type JSX } from 'react'
import { TargetsParam, animate } from 'animejs'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import {
  FobSpecialToken,
  getSpecialTokenUrl,
} from '@core/Areas/AppHeader/Components/Wallet/Components/helpers/useGetSpecialTokens'
import { useGetSpecialTokens } from '@core/Areas/AppHeader/Components/Wallet/Components/helpers/useGetSpecialTokens'
import { toggleIsDropDownOpen } from '@core/Areas/AppHeader/Store'
import { SetSuperpickStatus } from '@core/State/UserAccount/userAccountDriver'
import { useAppDispatch } from '@core/Store/hooks'
import { logError } from '@core/Utils/logging/logging'
import { appHistory } from '@core/Utils/routing/history'
import {
  AccountSuperPickBadgeStyled as Badge,
  AccountPromoButtonStyled as Button,
  AccountPromoButtonRowStyled as ButtonRow,
  AccountPromoCountStyled as Count,
  AccountPromoLabelStyled as Label,
  AccountPromoLinkStyled as Link,
  AccountPromoSuperpicksButtonStyled as SuperpicksButton,
  AccountPromoSuperpicksItemStyled as SuperpicksItem,
  AccountPromoSuperpicksListStyled as SuperpicksList,
  AccountPromoSuperpicksWrapStyled as SuperpicksWrap,
  AccountPromoWrapperStyled as Wrapper,
} from './OffersPanel.styles'

const NSW_LEGISLATION_ERROR_CODE = 190
const NSW_LEGISLATION_ERROR_ID = 'NSW_SUPERPICK_ERROR'

export const OffersPanel = ({
  bonusBetCount = 0,
  isOpen,
}: {
  bonusBetCount: number
  isOpen: boolean
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const superpicksWrapRef = React.useRef<HTMLDivElement>(null)
  const [isBonusSuperpicksExanded, setIsBonusSuperpicksExpanded] = React.useState(false)

  const { tokens, errorMessage, errorCode, isLoading: isLoadingSuperPicks } = useGetSpecialTokens()

  React.useEffect(() => {
    if (!superpicksWrapRef.current) return
    animate(superpicksWrapRef.current as TargetsParam, {
      height: isBonusSuperpicksExanded ? superpicksWrapRef.current.scrollHeight : '0px',
      ease: 'inOutQuad',
      duration: 300,
    })
  }, [isBonusSuperpicksExanded])

  React.useEffect(() => {
    if (!isOpen || isLoadingSuperPicks) {
      setIsBonusSuperpicksExpanded(false)
    }
  }, [isOpen, isLoadingSuperPicks])

  React.useEffect(() => {
    if (!isOpen && errorCode) removeToast({ id: NSW_LEGISLATION_ERROR_ID })
  }, [errorCode, isOpen])

  const handleSuperPickNav = (tokenId: number) => {
    if (tokenId) {
      SetSuperpickStatus({
        isLoading: true,
        tokens,
        errorMessage,
        errorCode,
      })

      getSpecialTokenUrl(tokenId)
        .then(result => {
          if (!result?.success || !result.data.SpecialUrl) {
            return
          }
          const nonHashUrl = '/' + result.data.SpecialUrl.replace(/^\/*#?\/*/, '')
          appHistory.push(nonHashUrl)
          dispatch(toggleIsDropDownOpen(false))
          SetSuperpickStatus({
            isLoading: false,
            tokens,
            errorMessage,
            errorCode,
          })
        })
        .catch(err => {
          SetSuperpickStatus({
            isLoading: false,
            tokens: [],
            errorMessage: 'Currently unable to retrieve SuperPicks',
            errorCode: null,
          })
          logError(err)
        })
    }
  }

  const standardSuperPicks = tokens.filter(token => !token.IsBonusToken)
  const bonusSuperPicks = tokens.filter(token => token.IsBonusToken)

  const isStandardSuperPickAvailable =
    !errorMessage && standardSuperPicks && standardSuperPicks.length > 0
  const isBonusSuperPicksAvailable = !errorMessage && bonusSuperPicks && bonusSuperPicks.length > 0

  const isNSWSuperPickError = errorMessage && errorCode === NSW_LEGISLATION_ERROR_CODE

  return (
    <Wrapper>
      <ButtonRow data-testid='dropdown-offers-row'>
        <div>
          <Button
            color='secondary'
            data-testid='account-dropdown-superpicks'
            disabled={!isNSWSuperPickError && !isStandardSuperPickAvailable}
            onClick={() => {
              if (isNSWSuperPickError) {
                addToast({
                  icon: 'error',
                  timeoutMs: Infinity,
                  title: errorMessage,
                  id: NSW_LEGISLATION_ERROR_ID,
                })
                return
              }
              if (isStandardSuperPickAvailable) handleSuperPickNav(standardSuperPicks[0].TokenId)
            }}
          >
            <Label data-tid-account-dropdown-superpicks-label>SuperPicks</Label>
            <Count>
              <span data-testid='account-dropdown-superpicks-count'>
                {isLoadingSuperPicks ? (
                  <SpinnerInlineStyled />
                ) : (
                  (isStandardSuperPickAvailable && standardSuperPicks[0].BetsAvailable) ||
                  (isNSWSuperPickError ? '!' : 0)
                )}
              </span>
            </Count>
          </Button>
        </div>

        <div>
          <Button
            color='secondary'
            data-tid-account-dropdown-bonus-superpicks
            disabled={!isBonusSuperPicksAvailable}
            onClick={() => {
              if (isBonusSuperPicksAvailable && !isLoadingSuperPicks)
                setIsBonusSuperpicksExpanded(state => !state)
            }}
          >
            <Label data-tid-account-dropdown-bonus-superpicks-label>
              Bonus
              <br />
              SuperPicks
            </Label>
            <Count>
              <span data-testid='account-dropdown-bonus-superpicks-count'>
                {isLoadingSuperPicks ? (
                  <SpinnerInlineStyled />
                ) : (
                  (isBonusSuperPicksAvailable && calculateTotalBonusSuperPicks(bonusSuperPicks)) ||
                  0
                )}
              </span>
            </Count>
          </Button>
        </div>

        <div>
          <Link
            color='secondary'
            to='/account/offer'
            data-tid-account-dropdown-bonus-bets
            disabled={false}
            onClick={() => {
              dispatch(toggleIsDropDownOpen(false))
            }}
          >
            <Label data-tid-account-dropdown-bonus-bets-label>Bonuses</Label>
            <Count>
              <span data-testid='account-dropdown-bonus-bets-count'>{bonusBetCount || 0}</span>
            </Count>
          </Link>
        </div>
      </ButtonRow>

      <SuperpicksWrap ref={superpicksWrapRef}>
        {bonusSuperPicks.length > 0 && (
          <SuperpicksList data-testid='account-dropdown-superpicks-list'>
            {bonusSuperPicks.map(({ TokenName, TokenId, BetsAvailable = 0 }) => (
              <SuperpicksItem key={TokenId}>
                <SuperpicksButton
                  onClick={() => {
                    handleSuperPickNav(TokenId)
                  }}
                >
                  {TokenName} <Badge>{BetsAvailable}</Badge>
                </SuperpicksButton>
              </SuperpicksItem>
            ))}
          </SuperpicksList>
        )}
      </SuperpicksWrap>
    </Wrapper>
  )
}

// =======
// Helpers
// =======
function calculateTotalBonusSuperPicks(array: FobSpecialToken[]): number {
  return array.reduce((count, { BetsAvailable = 0 }) => BetsAvailable + count, 0)
}
